import { Typography } from "@mui/material";
import type { FC } from "react";
import { INK, STATUS } from "@/theme/tokens";

/** Rounds to 1 decimal place, then drops the decimal if it's .0 */
export const formatCount = (n: number): string | number => {
	const rounded = Math.round(n * 10) / 10;
	return Number.isInteger(rounded) ? rounded : rounded.toFixed(1);
};

/** Single source of truth for the escalation thresholds so any other surface (e.g. a breakdown popover) can never drift from `CountBadge`'s own coloring. */
export const getStaffingStatus = (actual: number, required: number): { isUnderstaffed: boolean; isOverstaffed: boolean } => ({
	isUnderstaffed: required - actual >= 2,
	isOverstaffed: actual > required && required > 0,
});

/**
 * The ink and weight `CountBadge` prints a count in, beside the thresholds that decide them — so another
 * surface drawing the same `actual / required` (the mobile gaps-first home) colours it identically.
 */
export const getStaffingStatusStyle = (actual: number, required: number): { color: string; fontWeight: number; isFlagged: boolean } => {
	const { isUnderstaffed, isOverstaffed } = getStaffingStatus(actual, required);
	return {
		color: isUnderstaffed ? STATUS.ERROR.main : isOverstaffed ? STATUS.WARNING.main : INK.SECONDARY,
		fontWeight: isUnderstaffed || isOverstaffed ? 700 : 400,
		isFlagged: isUnderstaffed || isOverstaffed,
	};
};

export const CountBadge: FC<{ actual: number; required: number }> = ({ actual, required }) => {
	const { color, fontWeight } = getStaffingStatusStyle(actual, required);
	return (
		<Typography
			component="span"
			data-testid="weekly-count-badge"
			sx={{
				fontSize: "12px",
				fontWeight,
				color,
				whiteSpace: "nowrap",
			}}
		>
			{formatCount(actual)} / {formatCount(required)}
		</Typography>
	);
};
